/*
 * pingo-crosssell.js — by Studio Lab (Pingo build)
 *
 * Cross-sell card carousel inside the PDP main section. Pingo reads the curated
 * `custom.cross_sell` metafield, so cards are rendered server-side and present
 * on connect — there is NO recommendations-API hydration (no IntersectionObserver,
 * no Section Rendering fetch, no morphSection). This script only wires:
 *   - paged carousel (transform-based, one card per viewport)
 *   - variant <select> → updates visible price + installments via the
 *     per-card JSON blob (no extra fetches)
 *   - bag CTA → /cart/add.js + CartAddEvent (drawer opens unchanged)
 */

import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class PingoCrosssell extends Component {
  /** @type {number} */
  #currentIndex = 0;
  /** @type {boolean} */
  #wired = false;

  connectedCallback() {
    super.connectedCallback();
    // Curated data → cards are already in the DOM; wire immediately.
    this.#wireUI();
  }

  #wireUI() {
    if (this.#wired) return;
    this.#wired = true;

    const cards = this.querySelectorAll('[data-card]');
    if (!cards.length) return;

    this.#wireCarousel(cards.length);
    cards.forEach((card) => this.#wireCard(/** @type {HTMLElement} */ (card)));
  }

  /**
   * @param {number} slideCount
   */
  #wireCarousel(slideCount) {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    const counter = this.querySelector('[data-counter]');
    const nav = /** @type {HTMLElement | null} */ (this.querySelector('[data-nav]'));

    if (!track) return;

    if (slideCount <= 1) {
      nav?.setAttribute('hidden', '');
      return;
    }
    nav?.removeAttribute('hidden');

    const updateUI = () => {
      track.style.transform = `translateX(-${this.#currentIndex * 100}%)`;
      if (counter) counter.textContent = `${this.#currentIndex + 1}/${slideCount}`;
      if (prev) prev.toggleAttribute('disabled', this.#currentIndex === 0);
      if (next) next.toggleAttribute('disabled', this.#currentIndex === slideCount - 1);
    };

    prev?.addEventListener('click', () => {
      if (this.#currentIndex > 0) {
        this.#currentIndex--;
        updateUI();
      }
    });

    next?.addEventListener('click', () => {
      if (this.#currentIndex < slideCount - 1) {
        this.#currentIndex++;
        updateUI();
      }
    });

    updateUI();
  }

  /**
   * @param {HTMLElement} card
   */
  #wireCard(card) {
    /** @type {Record<string, { price: string, installments: string, available: boolean }>} */
    let variants = {};
    const dataEl = card.querySelector('script[data-variants]');
    if (dataEl?.textContent) {
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('[pingo-crosssell] bad variant JSON', e);
      }
    }

    const select = /** @type {HTMLSelectElement | null} */ (card.querySelector('[data-variant-select]'));
    const priceEl = card.querySelector('[data-price]');
    const installmentsEl = card.querySelector('[data-installments]');
    const addBtn = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-add]'));

    select?.addEventListener('change', () => {
      const id = select.value;
      const v = variants[id];
      if (!v) return;
      if (priceEl) priceEl.textContent = v.price;
      if (installmentsEl) installmentsEl.innerHTML = v.installments;
      if (addBtn) {
        addBtn.dataset.variantId = id;
        addBtn.disabled = !v.available;
      }
    });

    addBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled) return;
      this.#addToCart(variantId, addBtn);
    });
  }

  /**
   * @param {string} variantId
   * @param {HTMLButtonElement} addBtn
   */
  #addToCart(variantId, addBtn) {
    // Re-entrancy guard: the data-added attribute lives for 800ms after a
    // successful click (see end of this method). Treat that window as
    // "in flight" so spam-clicks don't queue extra cart-adds.
    if (addBtn.dataset.added === 'true') return;

    // Fire the click-feedback animation synchronously, mirroring the main
    // PDP button (assets/product-form.js calls animateAddToCart() inside
    // handleClick — not after the fetch resolves). Instant feedback regardless
    // of network latency.
    addBtn.dataset.added = 'true';
    setTimeout(() => addBtn.removeAttribute('data-added'), 800);

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    const payload = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionIds.join(','),
    };

    /** @type {{ routes?: { cart_add_url?: string } }} */
    const theme = /** @type {any} */ (window).Theme || {};
    const cartAddUrl = theme.routes?.cart_add_url || '/cart/add.js';

    // Horizon 4.x cart event (§5.3/§5.4): deferred-promise pattern. Dispatch a
    // CartLinesUpdateEvent carrying a promise BEFORE the fetch; native listeners
    // (cart icon/drawer) await it. Resolve with the FULL cart — currency is
    // required by createCartFromAjaxResponse and is absent from /cart/add.js.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.reject(new Error(response.message || 'Add to cart failed'));
          // Clear the optimistic feedback so a retry click works immediately.
          addBtn.removeAttribute('data-added');
          return;
        }
        // Full cart carries cart-level currency (the add response does not);
        // the add response is used only for its rendered .sections.
        const fullCart = await (await fetch('/cart.js')).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
          detail: {
            items: fullCart.items,
            source: 'pingo-crosssell',
            sourceId: this.id?.toString?.() || 'pingo-crosssell',
            itemCount: 1,
            variantId,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((err) => {
        console.warn('[pingo-crosssell] add-to-cart failed:', err);
        deferred.reject(err);
        // Clear the optimistic feedback so a retry click works immediately.
        addBtn.removeAttribute('data-added');
      });
  }
}

if (!customElements.get('pingo-crosssell')) {
  customElements.define('pingo-crosssell', PingoCrosssell);
}
